import { buildStatutoryInterestNote } from "@financialharmony/tax-se";
import { t } from "./catalog";
import type { DocumentLanguage, LineItem, PaymentMethod, Seller } from "./types";

export interface PreviewContentInput {
  seller: Seller | null;
  lines: LineItem[];
  /** An explicit override; falls back to the seller's resolved default, same as the backend. */
  paymentMethod?: PaymentMethod | null;
  freeText?: string;
  /** `null`/undefined renders no dröjsmålsränta note at all; `0` states an explicit "0 %". */
  interestRatePercent?: number | null;
  /** FIN-1580: the invoice document's own language — drives the payment-box headings/labels
   * below (AC1), independent of the viewer's own app UI language. Optional, default 'sv'
   * (this function's own established default, matching every pre-FIN-1580 caller unchanged). */
  documentLanguage?: DocumentLanguage;
}

export interface PreviewContent {
  /** Lines actually worth rendering — an all-empty new line renders nothing. */
  visibleLines: LineItem[];
  /** What this invoice is actually paid by, after applying the same fallback the backend uses. */
  paymentMethod: PaymentMethod;
  bankgiroNumber: string | null;
  /** Bankgiro renders the giro block in place of the account lines; bank account does the reverse. */
  showGiro: boolean;
  /** Trimmed; `null` when there's nothing to render. */
  freeText: string | null;
  /** The statutory dröjsmålsränta footer sentence, or `null` when no rate is stated. */
  statutoryNote: string | null;
  /** The payment box, as the PDF prints it (FIN-1173). */
  paymentBox: PaymentBox;
}

export interface PaymentBox {
  heading: string;
  /** What a payer in Sweden keys in, before the reference and the amount. */
  accountRows: Array<{ label: string; value: string }>;
  /** "OCR-nummer"/"OCR number" for bankgiro only; a bank-account payer writes a Message. */
  referenceLabel: string;
  /** IBAN/BIC, shown under "Från utlandet"/"From abroad". */
  foreignRows: Array<{ label: string; value: string }>;
}

const FOREIGN_PAYMENT_LABELS = new Set(["IBAN", "BIC"]);

/**
 * `seller.paymentReferenceLines` is built server-side with fixed Swedish labels
 * (invoiceGenerationService.buildPaymentReferenceLines) — it's the same generic seller-profile
 * data used by the form's account picker, computed before the invoice's own document language
 * is known, so it can't be localized at the source. "Bank"/"IBAN"/"BIC"/"Bankgiro"/"Plusgiro" are
 * the same word in both languages; only "Clearingnummer"/"Kontonummer" need translating here.
 */
function translatePaymentLabel(label: string, language: DocumentLanguage): string {
  if (label === "Clearingnummer") return t("payment.clearingNumberLabel", language);
  if (label === "Kontonummer") return t("payment.accountNumberLabel", language);
  return label;
}

function translateRows(rows: Array<{ label: string; value: string }>, language: DocumentLanguage): Array<{ label: string; value: string }> {
  return rows.map((row) => ({ ...row, label: translatePaymentLabel(row.label, language) }));
}

function buildPaymentBox(seller: Seller | null, showGiro: boolean, bankgiroNumber: string | null, language: DocumentLanguage): PaymentBox {
  // FIN-1580 (invoice-document-i18n.AC5): IBAN/BIC are shown whenever the seller's connected
  // account has them, regardless of payment method — previously hardcoded to [] for Bankgiro,
  // which suppressed an IBAN/BIC that exists purely because Bankgiro was the chosen method.
  // A Plusgiro-only seller (no connected account at all) still has none to show (EC2); a manual
  // bank account still has no BIC field (EC3) — both fall out of `paymentReferenceLines` itself
  // containing nothing to filter, not from a method-specific branch here.
  const lines = seller?.paymentReferenceLines ?? [];
  const foreignRows = lines.filter((l) => FOREIGN_PAYMENT_LABELS.has(l.label));
  if (showGiro) {
    return {
      heading: t("payment.bankgiroHeading", language),
      accountRows: [{ label: "Bankgiro", value: bankgiroNumber! }],
      referenceLabel: t("payment.ocrLabel", language),
      foreignRows: translateRows(foreignRows, language),
    };
  }
  return {
    heading: t("payment.bankAccountHeading", language),
    accountRows: translateRows(lines.filter((l) => !FOREIGN_PAYMENT_LABELS.has(l.label)), language),
    referenceLabel: t("payment.messageLabel", language),
    foreignRows: translateRows(foreignRows, language),
  };
}

/**
 * The preview's shared content model (US1.AC3) — what each line item, the giro/bank-account
 * block, and the free-text/statutory footer actually say, given a draft. Both apps' preview
 * components render this from their own markup/styling; nothing here is presentational.
 */
export function buildPreviewContent(input: PreviewContentInput): PreviewContent {
  const visibleLines = input.lines.filter((l) => l.description.trim() || l.quantity * l.unitPrice);
  // Falls back to what the backend derives from the company's bank accounts when the
  // sender has made no explicit choice — the same precedence it applies at render time.
  const paymentMethod = input.paymentMethod ?? input.seller?.resolvedPaymentMethod ?? "bank_account";
  // The receiving account's bankgiro (FIN-818), so an invoice switched to Bankgiro previews it
  // even while the seller's resolved reference lines are still the account details.
  // Then the company bankgiro saved without an account (FIN-1172), as the backend falls back to it.
  const bankgiroNumber = input.seller?.invoiceBankAccounts?.find((a) => a.receivesInvoices)?.bankgiro
    ?? input.seller?.companyBankgiro
    ?? input.seller?.paymentReferenceLines?.find((l) => l.label === "Bankgiro")?.value ?? null;
  const showGiro = paymentMethod === "bankgiro" && !!bankgiroNumber;
  const trimmedFreeText = input.freeText?.trim();
  const documentLanguage = input.documentLanguage ?? "sv";
  const statutoryNote = input.interestRatePercent != null
    ? buildStatutoryInterestNote(input.interestRatePercent, undefined, documentLanguage)
    : null;
  return {
    visibleLines,
    paymentMethod,
    bankgiroNumber,
    showGiro,
    freeText: trimmedFreeText ? trimmedFreeText : null,
    statutoryNote,
    paymentBox: buildPaymentBox(input.seller, showGiro, bankgiroNumber, documentLanguage),
  };
}
